import { apiFetch, getApiUrl } from "../auth/apiClient";

const d = (path, opts = {}) =>
  apiFetch(`/api/discussions${path}`, { authRequired: true, ...opts });

const qsOf = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.set(k, v);
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
};

export const discussionsApi = {
  // ===== Topics =====
  /** Pinned-first list (two-query server side). `q` title search, `before`
   *  is the unpinned keyset cursor "<iso>|<id>". */
  listTopics: ({ q, sort, before, limit } = {}) =>
    d(`/topics${qsOf({ q, sort, before, limit })}`),
  createTopic: (body) => d("/topics", { method: "POST", body }),
  getTopic: (id) => d(`/topics/${id}`),
  patchTopic: (id, body) => d(`/topics/${id}`, { method: "PATCH", body }),

  // moderation (board / admin only — server enforces)
  closeTopic: (id) => d(`/topics/${id}/close`, { method: "POST" }),
  reopenTopic: (id) => d(`/topics/${id}/reopen`, { method: "POST" }),
  pinTopic: (id) => d(`/topics/${id}/pin`, { method: "POST" }),
  unpinTopic: (id) => d(`/topics/${id}/unpin`, { method: "POST" }),
  deleteTopic: (id) => d(`/topics/${id}`, { method: "DELETE" }),

  // ===== Messages =====
  /** Dual/triple-cursor read (spec §10/§11):
   *  - history:   { before_seq } | none → latest DESC + next_before
   *  - append:    { after_seq }  → seq > n ASC
   *  - mutations: { changed_since, client_max_seq } → edited/deleted rows */
  listMessages: (topicId, { before_seq, after_seq, changed_since, client_max_seq, limit } = {}) =>
    d(`/topics/${topicId}/messages${qsOf({ before_seq, after_seq, changed_since, client_max_seq, limit })}`),

  /** Multipart send. FormData: body?, reply_to_message_id?, client_token,
   *  files[] (≤10). Idempotent on client_token server-side (§11). */
  sendMessage: (topicId, { body, replyToMessageId, clientToken, files } = {}) => {
    const fd = new FormData();
    if (body) fd.append("body", body);
    if (replyToMessageId) fd.append("reply_to_message_id", replyToMessageId);
    if (clientToken) fd.append("client_token", clientToken);
    (files || []).forEach((f) => fd.append("files", f));
    return d(`/topics/${topicId}/messages`, { method: "POST", formData: fd });
  },

  editMessage: (messageId, body) =>
    d(`/messages/${messageId}`, { method: "PATCH", body: { body } }),
  deleteMessage: (messageId) =>
    d(`/messages/${messageId}`, { method: "DELETE" }),
  reportMessage: (messageId, { reason, note } = {}) =>
    d(`/messages/${messageId}/report`, { method: "POST", body: { reason, note } }),
  /** Set/switch/clear the caller's like|dislike. `value` null clears it.
   *  Returns `{ message_id, like_count, dislike_count, my_reaction }`. */
  reactToMessage: (messageId, value) =>
    d(`/messages/${messageId}/reaction`, { method: "POST", body: { value } }),

  // ===== Block list (personal, one-directional; Play UGC "block users") =====
  /** Members the caller has blocked → `{ blocks: [{ user, created_at }] }`. */
  listBlocks: () => d("/blocks"),
  /** Block a member by user id. Idempotent → `{ ok, created }`. */
  blockUser: (userId) => d("/blocks", { method: "POST", body: { user_id: userId } }),
  /** Unblock a member. Idempotent → `{ ok }`. */
  unblockUser: (userId) => d(`/blocks/${userId}`, { method: "DELETE" }),

  // ===== Moderation: report queue (board / admin only — server enforces) =====
  /** OPEN reports newest-first, keyset-paginated `{ items, next_before }`. */
  listReports: ({ status = "OPEN", before, limit } = {}) =>
    d(`/reports${qsOf({ status, before, limit })}`),
  /** Resolve a queued report. `action` is "ACTIONED" | "DISMISSED". */
  resolveReport: (reportId, action) =>
    d(`/reports/${reportId}/resolve`, { method: "POST", body: { action } }),

  // ===== Read / subscription / nav badge =====
  markRead: (topicId, lastReadMessageId) =>
    d(`/topics/${topicId}/read`, {
      method: "POST",
      body: { last_read_message_id: lastReadMessageId || null },
    }),
  setSubscription: (topicId, mode) =>
    d(`/topics/${topicId}/subscription`, { method: "POST", body: { mode } }),
  unreadSummary: () => d("/unread-summary"),

  // ===== Media (hard-auth — fetched as authed blobs, see mediaBlobUrl) =====
  mediaUrl: (mediaId) => `/api/discussions/media/${mediaId}`,
  posterUrl: (mediaId) => `/api/discussions/media/${mediaId}/poster`,
};

/** WebSocket URL for a topic's live channel. JWT goes in `?token=` because
 *  browsers can't set Authorization on a WS handshake (spec §10). */
export function topicWsUrl(topicId, token) {
  const api = getApiUrl();
  const wsBase = api.replace(/^http/i, "ws");
  return `${wsBase}/api/discussions/topics/${topicId}/ws?token=${encodeURIComponent(token)}`;
}

/** Fetch hard-auth media bytes WITH the Bearer header and return an object
 *  URL (an <img>/<video> src can't carry the header). Caller must revoke
 *  the returned URL on unmount. Returns null on failure. */
export async function fetchMediaBlobUrl(path, { signal } = {}) {
  const token = localStorage.getItem("access_token");
  const res = await fetch(`${getApiUrl()}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });
  if (!res.ok) throw new Error(`media ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ---------------------------------------------------------------------------
// Display metadata + helpers
// ---------------------------------------------------------------------------

export const SUB_MODE_META = {
  DEFAULT: { label: "Default", hint: "Notified only when mentioned or quoted" },
  ALL: { label: "All messages", hint: "Notified on every new message" },
  MUTED: { label: "Muted", hint: "No notifications at all" },
};

export const REPORT_REASONS = [
  { value: "SPAM", label: "Spam" },
  { value: "ABUSE", label: "Abuse or harassment" },
  { value: "OFFTOPIC", label: "Off topic" },
  { value: "OTHER", label: "Something else" },
];

/** "99+" cap for unread badges. */
export function formatUnread(n) {
  if (!n || n <= 0) return null;
  return n > 99 ? "99+" : String(n);
}

/** Friendly relative-ish time: "10:42 AM" today, "Mon" within a week, else date. */
export function formatMessageTime(iso) {
  if (!iso) return "";
  const d2 = new Date(iso);
  if (Number.isNaN(d2.getTime())) return "";
  const now = new Date();
  const sameDay = d2.toDateString() === now.toDateString();
  if (sameDay) {
    return d2.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
  const diffDays = Math.floor((now - d2) / 86400000);
  if (diffDays < 7) {
    return d2.toLocaleDateString("en-IN", { weekday: "short" });
  }
  return d2.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: d2.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

/** Full date-time for tooltips: "Sun 5 Jul 2026, 10:00 AM". */
export function formatDateTime(iso) {
  if (!iso) return "—";
  const d2 = new Date(iso);
  if (Number.isNaN(d2.getTime())) return "—";
  const date = d2.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = d2.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${date}, ${time.toUpperCase()}`;
}

/** Friendly message from an apiFetch error. */
export function errMessage(e) {
  if (e && typeof e.detail === "string") return e.detail;
  if (e && e.message) return e.message;
  return "Something went wrong";
}

/** Build the unpinned keyset cursor a topic row would page from. */
export function topicCursor(topic) {
  const anchor = topic.last_message_at || topic.created_at;
  return anchor ? `${anchor}|${topic.id}` : null;
}
